// Import the core angular services.
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { Event as RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavigationEnd } from '@angular/router';
import { Observable } from 'rxjs';
import { Params } from '@angular/router';
import { pipe } from 'rxjs';
import { Router } from '@angular/router';

// ----------------------------------------------------------------------------------- //
// ----------------------------------------------------------------------------------- //

@Injectable({
  providedIn: 'root'
})
export class RouterParams {

  public params: BehaviorSubject<Params>;
  public paramsSnapshot: Params;

  private router: Router;

  // I initialize the router params service.
  constructor(router: Router) {

    this.router = router;

    this.paramsSnapshot = {};
    this.params = new BehaviorSubject(this.paramsSnapshot);

    // We will collection the params after every Router navigation event. However,
    // we're going to defer param aggregation until after the NavigationEnd event.
    // This should leave the Router in a predictable and steady state.
    this.router.events
      .pipe(
        filter(
          (event: RouterEvent): boolean => {

            return (event instanceof NavigationEnd);

          }
        )
      )
      .subscribe(
        (event: NavigationEnd): void => {

          const snapshot = this.router.routerState.snapshot.root;
          const nextParams = this.collectParams(snapshot);

          // A Router navigation event can occur for a variety of reasons, such
          // as a change to the search-params. As such, we need to inspect the
          // params to see if the structure actually changed with this
          // navigation event. If not, we don't want to emit an event.
          if (this.paramsAreDifferent(this.paramsSnapshot, nextParams))
            this.params.next(this.paramsSnapshot = nextParams);
        }
      )
      ;

  }

  private collectParams(root: ActivatedRouteSnapshot): Params {
    const params: any = {};
    (function mergeParamsFromSnapshot(snapshot: ActivatedRouteSnapshot) {
      Object.assign(params, snapshot.params, snapshot.queryParams);
      snapshot.children.forEach(mergeParamsFromSnapshot);
    })(root);
    return (params);
  }


  // I determine if the given param collections have a different [shallow] structure.
  private paramsAreDifferent(
    currentParams: Params,
    nextParams: Params
  ): boolean {

    const currentKeys = Object.keys(currentParams);
    const nextKeys = Object.keys(nextParams);

    // If the collection of keys in each set of params is different, then we know
    // that we have two unique collections.
    if (currentKeys.length !== nextKeys.length) {

      return (true);

    }

    // If the collections of keys have the same length then we have to start
    // comparing the individual KEYS and VALUES in each collection.
    for (let i = 0, length = currentKeys.length; i < length; i++) {

      const key = currentKeys[i];
      if (currentParams[key] !== nextParams[key])
        return (true);
    }
  }
}
